import { z } from 'zod';
/**
 * Area map v0 (92 §2 A6, 98 Q-19, doc 30 E17): the minimal JSON area-map
 * format that both the client and the Node server load.
 *
 * `walk` is a run-length-encoded, base64 byte-per-tile mask, row-major
 * (index = y * w + x). Each byte holds CELL_* bits:
 *   - CELL_WALK:   the tile is walkable (terrain, gutters AND static furniture
 *                  are already baked in; loaders must NOT re-block `objects`).
 *   - CELL_WALL_W: wall on the tile's W edge (between (x-1, y) and (x, y)).
 *   - CELL_WALL_N: wall on the tile's N edge (between (x, y-1) and (x, y)).
 * Wall bits match the engine's TileGrid.walls (WALL_W = 1, WALL_N = 2) shifted
 * left by one.
 *
 * RLE: a flat sequence of (runLength 1..255, value) byte pairs, then base64.
 *
 * `navVersion` = FNV-1a 32-bit over (w, h, decoded cells); see navVersion.ts.
 * It changes whenever any tile's walkability or wall bits change.
 */
export const CELL_WALK = 1;
export const CELL_WALL_W = 2;
export const CELL_WALL_N = 4;
export const CELL_MASK = CELL_WALK | CELL_WALL_W | CELL_WALL_N;
export const AREA_MAP_FORMAT_VERSION = 0;
const int = z.number().int();
const coord = int.min(0);
export const TilePointSchema = z.object({ x: coord, y: coord });
/** A placed static object the server/client can interact with (service objects, generator, ...). */
export const MapObjectSchema = z.object({
    /** Unique within the map; used on the wire (e.g. `Interact {obj}`). */
    oid: int.min(1),
    /** Object definition id, e.g. `svc.mama_put`. */
    def: z.string().min(1),
    /** Top corner tile of the footprint. */
    x: coord,
    y: coord,
    /** Quarter turns 0..3. */
    rot: int.min(0).max(3),
});
/** A compound room door. `x, y` is the walkable tile the player stands on to use it. */
export const MapDoorSchema = z.object({
    id: z.string().min(1),
    x: coord,
    y: coord,
    /** Room number within the compound (Room 1..6). */
    homeSlot: int.min(1),
});
/**
 * A hustle stall slot. `x, y` is the top corner of the 2x2 stand footprint
 * (already non-walkable in `walk`); `queue` lists customer tiles, front first.
 */
export const MapStallSchema = z.object({
    slot: int.min(1),
    x: coord,
    y: coord,
    queue: z.array(TilePointSchema),
});
export const AreaMapFileSchema = z.object({
    id: z.string().min(1),
    w: int.min(1).max(1024),
    h: int.min(1).max(1024),
    navVersion: int.min(0).max(0xffffffff),
    walk: z.string().min(1),
    objects: z.array(MapObjectSchema),
    doors: z.array(MapDoorSchema),
    stalls: z.array(MapStallSchema),
    /** Named points of interest (use tiles, NPC stand tiles, gates). */
    pois: z.record(z.string(), TilePointSchema),
    spawn: TilePointSchema,
});
